// Footer.jsx — Premium multi-column footer with MUI icons

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// MUI Icons
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import PinterestIcon from "@mui/icons-material/Pinterest";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const SHOP_LINKS = [
  { label: "All Gifts", to: "/products" },
  { label: "Gift Items", to: "/?category=Gift Items" },
  { label: "Toys & Games", to: "/?category=Toys" },
  { label: "Board Games", to: "/?category=Games" },
  { label: "Handicrafts", to: "/?category=Handicrafts" },
];

const OCCASION_LINKS = [
  { label: "Birthday Gifts", to: "/?occasion=birthday" },
  { label: "Anniversary Gifts", to: "/?occasion=anniversary" },
  { label: "Kids Gifts", to: "/?occasion=kids" },
  { label: "Festival Gifts", to: "/?occasion=festival" },
  { label: "Thank You Gifts", to: "/?occasion=thankyou" },
];

const ACCOUNT_LINKS = [
  { label: "My Account", to: "/login" },
  { label: "My Orders", to: "/orders" },
  { label: "Shopping Cart", to: "/cart" },
  { label: "Sign Up", to: "/signup" },
];

const SOCIAL = [
  { Icon: InstagramIcon, label: "Instagram" },
  { Icon: FacebookIcon, label: "Facebook" },
  { Icon: TwitterIcon, label: "Twitter" },
  { Icon: PinterestIcon, label: "Pinterest" },
];

const PAYMENT = [
  { Icon: CreditCardOutlinedIcon, label: "Cards" },
  { Icon: AccountBalanceOutlinedIcon, label: "Bank" },
  { Icon: PhoneAndroidOutlinedIcon, label: "Mobile" },
  { Icon: LockOutlinedIcon, label: "Secure" },
];

export default function Footer() {
  return (
    <footer style={{ background: "linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)" }} className="text-gray-400">

      {/* ── Top section ─────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-5 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-gift overflow-hidden">
                <img src="/images/logo.jpg" alt="Generic Ideas Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display text-xl font-bold text-white leading-none">Generic Ideas</div>
                <div className="text-[10px] text-gray-500 tracking-widest uppercase mt-0.5">Online Store</div>
              </div>
            </Link>

            <p className="text-sm leading-relaxed mb-6 max-w-xs">
              Curated products, handcrafted treasures, toys & games — bringing quality and joy to your everyday since 2020.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-3 mb-6">
              {SOCIAL.map(({ Icon, label }) => (
                <motion.button
                  key={label}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={label}
                  className="w-9 h-9 rounded-xl bg-white/8 hover:bg-rose-500/20 text-gray-400 hover:text-rose-400 flex items-center justify-center transition-colors border border-white/10"
                >
                  <Icon sx={{ fontSize: 18 }} />
                </motion.button>
              ))}
            </div>

            {/* Payment icons */}
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-widest font-semibold mb-3">Secure Payment</p>
              <div className="flex items-center gap-3">
                {PAYMENT.map(({ Icon, label }) => (
                  <div key={label} className="flex items-center gap-1 text-gray-500 hover:text-gray-300 transition-colors">
                    <Icon sx={{ fontSize: 22 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Shop column */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">Shop</h4>
            <ul className="space-y-3">
              {SHOP_LINKS.map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="flex items-center gap-1.5 text-sm hover:text-rose-400 transition-colors group">
                    <FiberManualRecordIcon sx={{ fontSize: 6, color: "#f0155a40" }} className="group-hover:text-rose-500 transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Occasions column */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">Occasions</h4>
            <ul className="space-y-3">
              {OCCASION_LINKS.map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="flex items-center gap-1.5 text-sm hover:text-rose-400 transition-colors group">
                    <FiberManualRecordIcon sx={{ fontSize: 6, color: "#8b5cf640" }} className="group-hover:text-purple-400 transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account + Contact column */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">My Account</h4>
            <ul className="space-y-3 mb-8">
              {ACCOUNT_LINKS.map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="flex items-center gap-1.5 text-sm hover:text-rose-400 transition-colors group">
                    <FiberManualRecordIcon sx={{ fontSize: 6, color: "#f59e0b40" }} className="group-hover:text-amber-400 transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Contact info */}
            <div className="space-y-4">
              {/* Emails */}
              <div className="flex items-start gap-2 text-sm">
                <EmailOutlinedIcon sx={{ fontSize: 16, mt: 0.5 }} />
                <div className="flex flex-col">
                  <span className="hover:text-white transition-colors cursor-pointer">info@genericideas.com</span>
                  <span className="hover:text-white transition-colors cursor-pointer">sales@genericideas.com</span>
                  <span className="hover:text-white transition-colors cursor-pointer">jessie@genericideas.com</span>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-2 text-sm">
                <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                <span className="hover:text-white transition-colors cursor-pointer">(903) 884-5765</span>
              </div>

              {/* Address */}
              <div className="flex items-start gap-2 text-sm">
                <LocationOnOutlinedIcon sx={{ fontSize: 16, mt: 0.5 }} />
                <div className="flex flex-col">
                  <span>Surrey, British Columbia, Canada</span>
                  <span>New Delhi, India | Mumbai, India</span>
                  <span>Reading RG1 7UP, UK</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────── */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} Generic Ideas Ltd. All rights reserved. Made with love for shoppers everywhere.
          </p>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</Link>
            <Link to="/refund" className="hover:text-white transition-colors cursor-pointer">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
